import { colorTokens } from "constants/colorTokens";
import { createTheme } from "@mui/material/styles";

/**
 * Create MUI theme based on current light/dark mode.
 * Light-mode palette is derived from colorTokens (synced with _variables.scss).
 * Dark-mode palette is provisional — dark tokens are not yet in _variables.scss.
 * @param {string} mode - 'light' or 'dark'
 * @returns {Theme} MUI theme object
 */
export const createMuiTheme = (mode = "light") => {
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? "#58a6ff" : colorTokens["primary"], // Ruby #ff0d5e
        light: isDark ? "#79c0ff" : colorTokens["primary-light"], // #fcbecf
        dark: isDark ? "#1f6feb" : colorTokens["primary-dark"], // #cc0a4b
        contrastText: colorTokens["basic-white"],
      },
      secondary: {
        main: isDark ? "#8957e5" : colorTokens["secondary"], // Amethyst #7a29a8
        light: isDark ? "#a371f7" : colorTokens["secondary-light"], // #e6baff
        dark: isDark ? "#7148bb" : colorTokens["secondary-dark"], // #622186
        contrastText: colorTokens["basic-white"],
      },
      success: {
        main: isDark ? "#3fb950" : colorTokens["success-primary"], // #38b95d
        light: isDark ? "#56d364" : colorTokens["success-light"], // #bfddc8
        dark: isDark ? "#2ea043" : colorTokens["success-dark"], // #30914c
      },
      warning: {
        main: isDark ? "#d29922" : colorTokens["warning-primary"], // #ffc600
        light: isDark ? "#e3b341" : colorTokens["warning-light"], // #ffedb0
        dark: isDark ? "#bb8009" : colorTokens["warning-dark"], // #cc9e00
      },
      error: {
        main: isDark ? "#ff7b72" : colorTokens["danger-primary"], // #ff2900
        light: isDark ? "#ffa198" : colorTokens["danger-light"], // #ffbdb0
        dark: isDark ? "#f85149" : colorTokens["danger-dark"], // #cc2100
      },
      info: {
        main: isDark ? "#79c0ff" : colorTokens["info-primary"], // #356de4
        light: isDark ? "#79c0ff" : colorTokens["info-light"], // #c0d2f7
        dark: isDark ? "#1f6feb" : colorTokens["info-dark"], // #2a57b6
      },
      background: {
        default: isDark ? "#0d1117" : colorTokens["basic-white"],
        paper: isDark ? "#161b22" : colorTokens["basic-white"],
      },
      text: {
        primary: isDark ? "#e6edf3" : colorTokens["body-default"], // #2a323a
        secondary: isDark ? "#8b949e" : colorTokens["neutral-grey-primary"], // #64748b
        disabled: isDark ? "#6e7681" : colorTokens["neutral-grey-dark"], // #334155
      },
      divider: isDark ? "#30363d" : colorTokens["neutral-grey-light"], // #cbd5e1
      action: {
        active: isDark ? "#58a6ff" : colorTokens["primary"],
        hover: isDark ? "rgba(88, 166, 255, 0.1)" : colorTokens["shadow-xs"],
        selected: isDark
          ? "rgba(88, 166, 255, 0.2)"
          : colorTokens["tertiary-ice-cool-light"],
        disabled: isDark
          ? "rgba(110, 118, 129, 0.3)"
          : colorTokens["shadow-sm"],
      },
    },
    typography: {
      fontFamily: [
        "Lato",
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
      fontSize: 14,
      h1: { fontSize: "2.5rem", fontWeight: 700 },
      h2: { fontSize: "2rem", fontWeight: 700 },
      h3: { fontSize: "1.75rem", fontWeight: 700 },
      h4: { fontSize: "1.5rem", fontWeight: 700 },
      h5: { fontSize: "1.25rem", fontWeight: 700 },
      h6: { fontSize: "1rem", fontWeight: 700 },
    },
    shape: {
      borderRadius: 4,
    },
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? "#30363d" : colorTokens["body-default"], // #2a323a
            color: isDark ? "#e6edf3" : colorTokens["basic-white"],
            fontSize: "0.875rem",
            padding: "0.5rem 0.75rem",
          },
          arrow: {
            color: isDark ? "#30363d" : colorTokens["body-default"],
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            "&.MuiChip-textual": {
              backgroundColor: "transparent",
              border: "none",
              color: colorTokens["body-default"],
            },
            "&.MuiChip-textual.MuiChip-colorPrimary": {
              color: colorTokens["primary"],
              "&:hover": { backgroundColor: colorTokens["primary-light"] },
            },
            "&.MuiChip-textual.MuiChip-colorSecondary": {
              color: colorTokens["secondary"],
              "&:hover": { backgroundColor: colorTokens["secondary-light"] },
            },
            "&.MuiChip-textual.MuiChip-colorSuccess": {
              color: colorTokens["success-primary"],
              "&:hover": { backgroundColor: colorTokens["success-light"] },
            },
            "&.MuiChip-textual.MuiChip-colorWarning": {
              color: colorTokens["warning-primary"],
              "&:hover": { backgroundColor: colorTokens["warning-light"] },
            },
            "&.MuiChip-textual.MuiChip-colorError": {
              color: colorTokens["danger-primary"],
              "&:hover": { backgroundColor: colorTokens["danger-light"] },
            },
            "&.MuiChip-textual.MuiChip-colorInfo": {
              color: colorTokens["info-primary"],
              "&:hover": { backgroundColor: colorTokens["info-light"] },
            },
          },
        },
        variants: [
          {
            props: { variant: "textual" },
            style: {},
          },
        ],
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: isDark ? "#e6edf3" : colorTokens["body-default"],
            "&:hover": {
              backgroundColor: isDark
                ? "#21262d"
                : colorTokens["neutral-grey-lighter"], // #f1f5f9
              color: isDark ? "#79c0ff" : colorTokens["primary"],
            },
            "&.Mui-focusVisible": {
              backgroundColor: isDark
                ? "#21262d"
                : colorTokens["neutral-grey-lighter"],
              color: isDark ? "#79c0ff" : colorTokens["primary"],
            },
            "&.Mui-selected": {
              backgroundColor: isDark ? "#58a6ff" : colorTokens["primary"],
              color: colorTokens["basic-white"],
              "&:hover": {
                backgroundColor: isDark
                  ? "#1f6feb"
                  : colorTokens["primary-dark"],
                color: colorTokens["basic-white"],
              },
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? "#161b22" : colorTokens["basic-white"],
            borderColor: isDark ? "#30363d" : colorTokens["neutral-grey-light"], // #cbd5e1
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontSize: "0.875rem",
          },

          /* Contained variants */
          containedPrimary: {
            backgroundColor: colorTokens["primary"],
            color: colorTokens["basic-white"],
            "&:hover": {
              backgroundColor: colorTokens["primary-dark"],
            },
          },
          containedSecondary: {
            backgroundColor: colorTokens["secondary"],
            color: colorTokens["basic-white"],
            "&:hover": {
              backgroundColor: colorTokens["secondary-dark"],
            },
          },
          containedSuccess: {
            backgroundColor: colorTokens["success-primary"],
            color: colorTokens["basic-white"],
            "&:hover": {
              backgroundColor: colorTokens["success-dark"],
            },
          },
          containedWarning: {
            backgroundColor: colorTokens["warning-primary"],
            color: colorTokens["basic-white"],
            "&:hover": {
              backgroundColor: colorTokens["warning-dark"],
            },
          },
          containedError: {
            backgroundColor: colorTokens["danger-primary"],
            color: colorTokens["basic-white"],
            "&:hover": {
              backgroundColor: colorTokens["danger-dark"],
            },
          },
          containedInfo: {
            backgroundColor: colorTokens["info-primary"],
            color: colorTokens["basic-white"],
            "&:hover": {
              backgroundColor: colorTokens["info-dark"],
            },
          },

          /* Outlined variants */
          "outlined-primary": {
            color: colorTokens["primary"],
            borderColor: colorTokens["primary"],
            "&:hover": {
              backgroundColor: colorTokens["primary-light"],
            },
          },
          "outlined-secondary": {
            color: colorTokens["secondary"],
            borderColor: colorTokens["secondary"],
            "&:hover": {
              backgroundColor: colorTokens["secondary-light"],
            },
          },
          "outlined-success": {
            color: colorTokens["success-primary"],
            borderColor: colorTokens["success-primary"],
            "&:hover": {
              backgroundColor: colorTokens["success-light"],
            },
          },
          "outlined-warning": {
            color: colorTokens["warning-primary"],
            borderColor: colorTokens["warning-primary"],
            "&:hover": {
              backgroundColor: colorTokens["warning-light"],
            },
          },
          "outlined-error": {
            color: colorTokens["danger-primary"],
            borderColor: colorTokens["danger-primary"],
            "&:hover": {
              backgroundColor: colorTokens["danger-light"],
            },
          },
          "outlined-info": {
            color: colorTokens["info-primary"],
            borderColor: colorTokens["info-primary"],
            "&:hover": {
              backgroundColor: colorTokens["info-light"],
            },
          },

          /* Textual variants */
          "textual-primary": {
            color: colorTokens["primary"],
            "&:hover": {
              backgroundColor: colorTokens["primary-light"],
            },
          },
          "textual-secondary": {
            color: colorTokens["secondary"],
            "&:hover": {
              backgroundColor: colorTokens["secondary-light"],
            },
          },
          "textual-success": {
            color: colorTokens["success-primary"],
            "&:hover": {
              backgroundColor: colorTokens["success-light"],
            },
          },
          "textual-warning": {
            color: colorTokens["warning-primary"],
            "&:hover": {
              backgroundColor: colorTokens["warning-light"],
            },
          },
          "textual-error": {
            color: colorTokens["danger-primary"],
            "&:hover": {
              backgroundColor: colorTokens["danger-light"],
            },
          },
          "textual-info": {
            color: colorTokens["info-primary"],
            "&:hover": {
              backgroundColor: colorTokens["info-light"],
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: colorTokens["tertiary-sapphire-light"],
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colorTokens["tertiary-sapphire-light"],
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            color: isDark ? "#e6edf3" : colorTokens["body-default"],
            "&.Mui-disabled": {
              color: isDark ? "#6e7681" : colorTokens["neutral-grey-primary"],
              WebkitTextFillColor: isDark
                ? "#6e7681"
                : colorTokens["neutral-grey-primary"],
            },

            "&:focus": {
              backgroundColor: "transparent",
            },
          },
          icon: {
            color: isDark ? "#58a6ff" : colorTokens["primary"],
            "&.Mui-disabled": {
              color: isDark ? "#6e7681" : colorTokens["neutral-grey-primary"],
            },
          },
          iconOpen: {
            color: isDark ? "#58a6ff" : colorTokens["primary"],
          },
        },
      },
    },
  });
};
